import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import PAMMFundForm, {
  PAMMFundFormValues,
} from '../components/shared/PAMMFundForm';
import { pammFundService } from '../services/pammService';
import { PAMM_ROUTES } from '../../../constants/routeConstants';
import toast from 'react-hot-toast';
import type { PAMMFund } from '../types';

export default function EditFund() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [fund, setFund] = useState<PAMMFund | null>(null);
  const [formValues, setFormValues] = useState<Partial<PAMMFundFormValues>>({});
  const [selectedSymbols, setSelectedSymbols] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await pammFundService.getById(id);
      if (!data) throw new Error('Fund not found');
      setFund(data);
      setFormValues({
        name: data.name,
        description: data.description,
        currency: data.currency,
        minimumInvestment: data.minimumInvestment,
        maximumInvestment: data.maximumInvestment,
        performanceFee: data.performanceFee,
        managementFee: data.managementFee,
        entryFee: data.entryFee,
        exitFee: data.exitFee,
        riskLevel: data.riskLevel,
        leverage: data.leverage,
        allocationMethod: data.allocationMethod,
        lockPeriodDays: data.lockPeriodDays,
        status: data.status,
      });
      setSelectedSymbols(data.tradingSymbols || []);
    } catch {
      setError('Failed to load fund details.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [id]);

  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
      const scrollEl = document.querySelector('main .overflow-y-auto');
      if (scrollEl) {
        scrollEl.scrollTop = 0;
      }
    }
  }, [loading]);

  const handleSubmit = async (
    submittedValues: PAMMFundFormValues,
    symbols: string[]
  ) => {
    if (!id) return;
    try {
      setSaving(true);
      await pammFundService.update(id, {
        ...submittedValues,
        minimumInvestment: Number(submittedValues.minimumInvestment),
        maximumInvestment: Number(submittedValues.maximumInvestment),
        performanceFee: Number(submittedValues.performanceFee),
        managementFee: Number(submittedValues.managementFee),
        entryFee: Number(submittedValues.entryFee),
        exitFee: Number(submittedValues.exitFee),
        lockPeriodDays: Number(submittedValues.lockPeriodDays),
        tradingSymbols: symbols,
      });
      toast.success('Fund updated successfully!');
      navigate(`${PAMM_ROUTES.PAMM_FUND_DETAILS}/${id}`);
    } catch {
      toast.error('Failed to update fund.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <PageHeader title="Edit Fund" />
        <LoadingSkeleton type="form" rows={6} />
      </div>
    );
  }

  if (error || !fund) {
    return (
      <div className="space-y-4">
        <PageHeader title="Edit Fund" />
        <PAMMErrorState message={error ?? 'Fund not found.'} onRetry={load} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Edit Fund — ${fund.name}`}
        description="Update fund configuration and settings"
      />
      <PAMMFundForm
        mode="edit"
        initialValues={formValues}
        initialSymbols={selectedSymbols}
        submitting={saving}
        onSubmit={handleSubmit}
        onCancel={() => navigate(-1)}
      />
    </div>
  );
}
